import { cva } from "../../lib";

// Maps each semantic color token to its Tailwind text class. Exported so other
// atoms (e.g. Tag icons) can color content to match Typography tokens.
export const typographyColorClass = {
  "neutral-foreground-default": "text-luce-neutral-foreground-default",
  "neutral-foreground-intermediate": "text-luce-neutral-foreground-intermediate",
  "neutral-foreground-attenuated": "text-luce-neutral-foreground-attenuated",
  "neutral-foreground-inverse": "text-luce-neutral-foreground-inverse",
  "brand-primary": "text-luce-brand-primary",
  "brand-primary-hover": "text-luce-brand-primaryHover",
  "brand-on-primary": "text-luce-brand-onPrimary",
  "brand-primary-surface": "text-luce-brand-primarySurface",
  "brand-on-primary-surface": "text-luce-brand-onPrimarySurface",
  "brand-primary-border": "text-luce-brand-primaryBorder",
  "brand-static": "text-luce-brand-static",
  "brand-static-hover": "text-luce-brand-staticHover",
  "brand-on-static": "text-luce-brand-onStatic",
  "brand-on-static-attenuated": "text-luce-brand-onStaticAttenuated",
  "brand-static-border": "text-luce-brand-staticBorder",
  "button-cta-default": "text-luce-button-cta-default",
  "button-cta-hover": "text-luce-button-cta-hover",
  "button-cta-on-cta": "text-luce-button-cta-onCta",
  "button-cta-on-cta-disabled": "text-luce-button-cta-onCtaDisabled",
  "button-cta-surface": "text-luce-button-cta-surface",
  "button-cta-surface-hover": "text-luce-button-cta-surfaceHover",
  "button-cta-on-surface": "text-luce-button-cta-onSurface",
  "button-cta2-default": "text-luce-button-cta2-default",
  "button-cta2-hover": "text-luce-button-cta2-hover",
  "button-cta2-on-cta2": "text-luce-button-cta2-onCta2",
  "button-cta2-on-cta2-disabled": "text-luce-button-cta2-onCta2Disabled",
  "button-cta2-on-surface": "text-luce-button-cta2-onSurface",
  "button-destructive-on-destructive": "text-luce-button-destructive-onDestructive",
  "button-destructive-on-destructive-disabled":
    "text-luce-button-destructive-onDestructiveDisabled",
  "button-destructive-on-surface": "text-luce-button-destructive-onSurface",
  "button-destructive-default": "text-luce-button-destructive-default",
  "button-disabled-cta-text-disabled": "text-luce-button-disabled-ctaTextDisabled",
  "button-success-default": "text-luce-button-success-default",
  "system-success-default": "text-luce-system-success-default",
  "system-success-on-success": "text-luce-system-success-onSuccess",
  "system-success-on-surface": "text-luce-system-success-onSurface",
  "system-info-default": "text-luce-system-info-default",
  "system-info-on-info": "text-luce-system-info-onInfo",
  "system-info-on-surface": "text-luce-system-info-onSurface",
  "system-danger-default": "text-luce-system-danger-default",
  "system-danger-on-danger": "text-luce-system-danger-onDanger",
  "system-danger-on-surface": "text-luce-system-danger-onSurface",
  "system-warning-default": "text-luce-system-warning-default",
  "system-warning-on-warning": "text-luce-system-warning-onWarning",
  "system-warning-on-surface": "text-luce-system-warning-onSurface",
} as const;

// By default, semantic classes (h1, h2, ...) already include font weight on web.
// Native does not get those semantic weights, so variants include native font classes.
export const typographyVariants = cva("font-sans", {
  variants: {
    variant: {
      "display-h1": "text-display-h1 md:text-display-h1-dt native:font-inter-bold",
      "display-h2": "text-display-h2 md:text-display-h2-dt native:font-inter-bold",
      "display-h3": "text-display-h3 md:text-display-h3-dt native:font-inter-bold",
      "heading-h1": "text-heading-h1 md:text-heading-h1-dt native:font-inter-semibold",
      "heading-h2": "text-heading-h2 md:text-heading-h2-dt native:font-inter-semibold",
      "heading-h3": "text-heading-h3 md:text-heading-h3-dt native:font-inter-semibold",
      "label-lg": "text-label-lg md:text-label-lg-dt native:font-inter-medium",
      "label-md": "text-label-md md:text-label-md-dt native:font-inter-medium",
      "label-sm": "text-label-sm md:text-label-sm-dt native:font-inter-medium",
      "body-lg": "text-body-lg md:text-body-lg-dt native:font-inter-regular",
      "body-md": "text-body-md md:text-body-md-dt native:font-inter-regular",
      "body-sm": "text-body-sm md:text-body-sm-dt native:font-inter-regular",
      "caption-md": "text-caption-md md:text-caption-md-dt native:font-inter-regular",
      "caption-sm": "text-caption-sm md:text-caption-sm-dt native:font-inter-regular",
      // The same sizes at Medium (500), where Figma sets the style to Medium: Tag
      // (caption-md, body-sm and body-md by size) and TagRibbon's small size
      // (body-sm). `md:font-medium` beats the desktop composite's 400.
      "body-md-medium":
        "text-body-md md:text-body-md-dt native:font-inter-medium font-medium md:font-medium",
      "body-sm-medium":
        "text-body-sm md:text-body-sm-dt native:font-inter-medium font-medium md:font-medium",
      "caption-md-medium":
        "text-caption-md md:text-caption-md-dt native:font-inter-medium font-medium md:font-medium",
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
      justify: "text-justify",
    },
    transform: {
      none: "normal-case",
      uppercase: "uppercase",
      lowercase: "lowercase",
      capitalize: "capitalize",
    },
    color: typographyColorClass,
    whiteSpace: {
      normal: "whitespace-normal",
      nowrap: "whitespace-nowrap",
      pre: "whitespace-pre",
      "pre-line": "whitespace-pre-line",
      "pre-wrap": "whitespace-pre-wrap",
    },
    italic: {
      true: "italic",
      false: "",
    },
    strike: {
      true: "line-through",
      false: "",
    },
  },
  defaultVariants: {
    variant: "body-md",
    color: "neutral-foreground-default",
    italic: false,
    strike: false,
  },
});
